import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import tcs from '../../../assets/images/career/tcs.webp'
import galleryImage1 from '../../../assets/images/gallery/galleryimage1.webp'
import galleryImage2 from '../../../assets/images/gallery/galleryimage2.webp'
import galleryImage3 from '../../../assets/images/gallery/galleryimage3.webp'
import galleryImage4 from '../../../assets/images/gallery/galleryimage4.webp'
import galleryImage5 from '../../../assets/images/gallery/galleryimage5.webp'
import galleryImage6 from '../../../assets/images/gallery/galleryimage6.webp'
import galleryImage7 from '../../../assets/images/gallery/galleryimage7.webp'
import galleryImage8 from '../../../assets/images/gallery/galleryimage8.webp'
import galleryImage10 from '../../../assets/images/gallery/galleryimage10.webp'
import galleryImage11 from '../../../assets/images/gallery/galleryimage11.webp'
import galleryImage12 from '../../../assets/images/gallery/galleryimage12.webp'
import galleryImage13 from '../../../assets/images/gallery/galleryimage13.webp'
import galleryImage14 from '../../../assets/images/gallery/galleryimage14.webp'
import galleryImage17 from '../../../assets/images/gallery/galleryimage17.webp'
import Constants from '../../../app/utils/constants'

const headingGradientStyle = {
  background: 'linear-gradient(92.21deg, #EA892A 0%, #E35384 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
} as const

const cardVariants = {
  hidden: { opacity: 0, scale: 0.75 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 2.05,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
}

const cardsContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
}

const imageTransition = {
  duration: 1.9,
  ease: [0.22, 1, 0.36, 1] as const,
}

const topImageSequence = [tcs, galleryImage1, galleryImage5, galleryImage7, galleryImage8]
const secondImageSequence = [galleryImage2, galleryImage11, galleryImage12, galleryImage14, galleryImage17]
const thirdImageSequence = [galleryImage3, galleryImage4, galleryImage6, galleryImage10, galleryImage13]

const renderCardHeading = (heading: string, cardIndex: number) => {
  const cleanHeading = heading.replace(':', '').trim()
  const isGradientHeading = cardIndex % 2 === 1

  return (
    <>
      <span
        className={isGradientHeading ? '' : 'text-white'}
        style={isGradientHeading ? headingGradientStyle : undefined}
      >
        {cleanHeading}
      </span>
      <span className={isGradientHeading ? 'text-white' : 'text-purple'}>
        {' '}
        :
      </span>
    </>
  )
}

const WhyEngineers = () => {
  const [topImageStep, setTopImageStep] = useState(0)
  const [secondImageStep, setSecondImageStep] = useState(0)
  const [thirdImageStep, setThirdImageStep] = useState(0)

  useEffect(() => {
    const topDelay = window.setTimeout(() => {
      setTopImageStep((prev) => prev + 1)
    }, 800)

    const secondDelay = window.setTimeout(() => {
      setSecondImageStep((prev) => prev + 1)
    }, 1800)

    const thirdDelay = window.setTimeout(() => {
      setThirdImageStep((prev) => prev + 1)
    }, 2900)

    const topId = window.setInterval(() => {
      setTopImageStep((prev) => prev + 1)
    }, 4800)

    const secondId = window.setInterval(() => {
      setSecondImageStep((prev) => prev + 1)
    }, 6200)

    const thirdId = window.setInterval(() => {
      setThirdImageStep((prev) => prev + 1)
    }, 7600)

    return () => {
      window.clearTimeout(topDelay)
      window.clearTimeout(secondDelay)
      window.clearTimeout(thirdDelay)

      window.clearInterval(topId)
      window.clearInterval(secondId)
      window.clearInterval(thirdId)
    }
  }, [])

  const topImage = topImageSequence[topImageStep % topImageSequence.length]
  const secondImage = secondImageSequence[secondImageStep % secondImageSequence.length]
  const thirdImage = thirdImageSequence[thirdImageStep % thirdImageSequence.length]

  return (
    <section className="mx-auto w-[90%] py-14 md:py-16 lg:py-20">
      <div className="lg:z-20 lg:bg-[#090129] lg:pt-6">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="font-rajdhani text-3xl font-semibold text-white md:text-4xl lg:text-4xl xl:text-5xl 2xl:text-7xl">
            Why Engineers Choose
          </h2>

          <h3 className="mt-2 font-rajdhani text-3xl font-semibold text-purple md:text-4xl lg:text-4xl xl:text-5xl 2xl:text-7xl">
            The Coding Studio
          </h3>
        </div>

        <div className="mt-10 grid items-stretch gap-6 lg:grid-cols-[1fr_1.5fr] lg:gap-8">

          {/* HIDE IMAGE SECTION IN MOBILE VIEW */}
          <div className="relative mx-auto hidden w-full max-w-[920px] aspect-920/800 h-[420px] sm:h-[500px] md:h-[500px] md:block lg:h-[360px] xl:h-[480px] 2xl:h-[800px]">

            <div className="absolute inset-y-0 left-0 w-[60%] overflow-hidden rounded-[30px]">
              <div className="relative h-full w-full overflow-hidden rounded-[30px]">
                <AnimatePresence initial={false} mode="sync">
                  <motion.img
                    key={`top-${topImage}-${topImageStep}`}
                    src={topImage}
                    alt="Engineering team collaboration"
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={imageTransition}
                  />
                </AnimatePresence>
              </div>
            </div>

            <div className="absolute bottom-[1.5%] right-0 z-10 w-[38%] overflow-hidden rounded-[30px]">
              <div className="relative aspect-square w-full overflow-hidden rounded-[30px]">
                <AnimatePresence initial={false} mode="sync">
                  <motion.img
                    key={`second-${thirdImage}-${thirdImageStep}`}
                    src={thirdImage}
                    alt="Engineering details"
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={imageTransition}
                  />
                </AnimatePresence>
              </div>
            </div>

            <div className="absolute right-0 z-20 w-[55%] -translate-x-[22%] overflow-hidden rounded-[30px] ring-[4px] ring-[#0E0924] md:bottom-[calc(48%+0.2rem)] lg:bottom-[calc(49%+0.2rem)] xl:bottom-[calc(49%+0.6rem)] 2xl:bottom-[calc(49%+0.2rem)]">
              <div className="relative aspect-3/2 w-full overflow-hidden rounded-[30px]">
                <AnimatePresence initial={false} mode="sync">
                  <motion.img
                    key={`third-${secondImage}-${secondImageStep}`}
                    src={secondImage}
                    alt="Team collaboration showcase"
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={imageTransition}
                  />
                </AnimatePresence>
              </div>
            </div>
          </div>

          <motion.div
            className="grid h-fit gap-7 md:gap-4 2xl:gap-6"
            variants={cardsContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
          >
            {/* HIDE "Meaningful Work" CARD IN MOBILE VIEW */}
            {Constants.whyEngineerCards.map((card, idx) => {
              const isMeaningfulWork =
                card.heading === 'Meaningful Work :'

              return (
                <motion.article
                  key={`${card.heading}-${idx}`}
                  variants={cardVariants}
                  className={`relative h-fit w-full rounded-[30px] p-[1.5px] ${
                    isMeaningfulWork ? 'hidden md:block' : ''
                  }`}
                  style={{
                    background:
                      'linear-gradient(101.26deg, rgba(116, 77, 255, 0.6) 0%, rgba(116, 77, 255, 0.15) 50%, rgba(116, 77, 255, 0.6) 100%)',
                  }}
                >
                  <div className="relative overflow-hidden rounded-[30px] bg-[#0E0924] p-5 md:p-6 lg:px-5 lg:py-2 xl:p-5 2xl:p-8">

                    <span className="pointer-events-none absolute -left-12 -top-12 h-[50%] w-[20%] rounded-full bg-purple/25 blur-2xl" />

                    <span className="pointer-events-none absolute -bottom-12 -right-12 h-[50%] w-[20%] rounded-full bg-purple/10 blur-2xl" />

                    <h4 className="relative mb-1 z-10 font-gilroy-medium text-xl md:text-xl lg:text-sm xl:text-base 2xl:text-2xl">
                      {renderCardHeading(card.heading, idx)}
                    </h4>

                    <p className="relative z-10 w-[95%] font-gilroy-medium text-sm leading-tight text-gray-400 md:text-sm lg:text-xs xl:text-sm 2xl:mt-3 2xl:text-xl">
                      {card.description}
                    </p>

                  </div>
                </motion.article>
              )
            })}
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default WhyEngineers